// src/pages/ProductPage/ProductCard.js

import React, { useState, useEffect } from 'react';
import { Card, Tooltip, Rate, Collapse, List, Typography, Button, Modal, Form, Input, message, Popconfirm } from 'antd';
import { FaTag, FaShoppingCart } from 'react-icons/fa';  // Category icons
import { PlusOutlined, HeartOutlined, HeartFilled, DeleteOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/DBContext';  // Access review functions
const { Meta } = Card;
const { Panel } = Collapse;
const { Text } = Typography;
const { TextArea } = Input;

const ProductCard = ({ productId, productName, description, categories, imageUrl, productLink }) => {
    const { 
        currentUser, 
        addReviewToProduct, 
        fetchReviewsForProduct, 
        addFavoriteProduct, 
        removeFavoriteProduct, 
        userInfo,
        deleteProductReview 
    } = useAuth();

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [reviews, setReviews] = useState([]);
    const [averageRating, setAverageRating] = useState(0);
    const [visibleReviews, setVisibleReviews] = useState(2); // State to control visible reviews

    // Fetch reviews on component mount
    useEffect(() => {
        if (!productId) return;

        const unsubscribe = fetchReviewsForProduct(productId, (fetchedReviews) => {
            setReviews(fetchedReviews);
            calculateAverageRating(fetchedReviews);
        });

        // Cleanup listener on unmount
        return () => {
            unsubscribe();
        };
    }, [productId, fetchReviewsForProduct]);

    // Function to calculate average rating
    const calculateAverageRating = (reviews) => {
        if (reviews.length === 0) {
            setAverageRating(0);
            return;
        }
        const total = reviews.reduce((acc, review) => acc + review.rating, 0);
        const avg = total / reviews.length;
        setAverageRating(parseFloat(avg.toFixed(1)));
    };

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            const { rating, comment } = values;

            // Add review to Firebase
            await addReviewToProduct(productId, rating, comment);

            message.success("Review added successfully!");
            form.resetFields();
            setIsModalVisible(false);
        } catch (error) {
            // console.error('Error submitting review:', error);
            message.error("Failed to add review. Please try again.");
        }
    };

    // Function to render category icons based on category name
    const renderCategoryIcon = (category) => {
        switch (category) {
            case 'Category 1':
                return <FaTag />;
            case 'Category 2':
                return <FaShoppingCart />;
            default:
                return <FaTag />;
        }
    };


    const [isFavorite, setIsFavorite] = useState(false);

    useEffect(() => {
        if (userInfo?.favoriteProducts) {
            setIsFavorite(userInfo.favoriteProducts.includes(productId));
        }
    }, [userInfo, productId]);

    const toggleFavorite = async () => {
        if (!currentUser) {
            message.error('Please sign in to favorite products.');
            return;
        }

        try {
            if (isFavorite) {
                await removeFavoriteProduct(userInfo.id, productId);
                message.success('Product removed from favorites.');
            } else {
                await addFavoriteProduct(userInfo.id, productId);
                message.success('Product added to favorites.');
            }
        } catch (error) {
            message.error('Failed to update favorites.');
        }
    };

    const handleDeleteReview = async (reviewId) => {
        try {
            await deleteProductReview(productId, reviewId);
            message.success('Review deleted successfully');
        } catch (error) {
            // console.error('Error deleting review:', error);
            message.error('Failed to delete review');
        }
    };

    return (
        <>
            <Card
                hoverable
                style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
                cover={
                    <a href={productLink} target="_blank" rel="noopener noreferrer">
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '200px',
                                overflow: 'hidden',
                                backgroundColor: '#f5f5f5',
                            }}
                        >
                            <img
                                alt={productName}
                                src={imageUrl}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                }}
                            />
                        </div>
                    </a>
                }
                actions={[
                
                    <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>

                           
                        <Rate 
                            disabled 
                            value={averageRating}  // Changed from defaultValue to value
                            style={{ fontSize: '14px', marginLeft: '15px' }} 
                        />
                        <span style={{ marginLeft: '15px' }}>{averageRating} / 5</span>

                        <Tooltip style={{marginLeft: '100px', marginTop: '10px'}} title={isFavorite ? "Unfavorite Product" : "Favorite Product"}>
                    <Button 
                        type="text" 
                        icon={isFavorite ? <HeartFilled style={{ color: 'red' }} /> : <HeartOutlined />} 
                        onClick={toggleFavorite} 
                    />
                </Tooltip>
                    </div>,
                ]}
            >
                <Meta
                    title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ color: '#1890ff', fontWeight: 'bold' }}>
                                {productName}
                            </div>
                            <Button 
                                type="primary" 
                                size="small" 
                                onClick={() => window.open(productLink, '_blank')}
                            >
                                Check Out Now!
                            </Button>
                        </div>
                    }
                    
                    description={description}
                />
                <div style={{ marginTop: '10px', flexGrow: 1 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        {categories.map((category, index) => (
                            <Tooltip title={category} key={index}>
                                <span style={{ marginRight: '10px', display: 'flex', alignItems: 'center' }}>
                                    {renderCategoryIcon(category)}
                                    <Text style={{ marginLeft: '5px' }}>{category}</Text>
                                </span>
                            </Tooltip>
                        ))}
                    </div>
                </div>
                {/* Increased margin to add space between categories and reviews */}
                <div style={{ marginTop: '20px',  }}>
                    <Collapse>
                        <Panel header="Reviews" key="1">
                            {reviews.length === 0 ? (
                                <Text>No reviews yet. Be the first to review this product!</Text>
                            ) : (
                                <List
                                    itemLayout="horizontal"
                                    dataSource={reviews.slice(0, visibleReviews)} // Only show visible reviews
                                    renderItem={item => (
                                        <List.Item
                                            actions={userInfo?.role === 'admin' ? [
                                                <Popconfirm
                                                    title="Delete Review"
                                                    description="Are you sure you want to delete this review?"
                                                    onConfirm={() => handleDeleteReview(item.id)}
                                                    okText="Yes"
                                                    cancelText="No"
                                                >
                                                    <Button 
                                                        type="text" 
                                                        danger 
                                                        icon={<DeleteOutlined />}
                                                    >
                                                        Delete
                                                    </Button>
                                                </Popconfirm>
                                            ] : []}
                                        >
                                            <List.Item.Meta
                                                title={<a href="#!" style={{ color: '#1890ff' }}>{item.userName}</a>}
                                                description={
                                                    <>
                                                        <Rate 
                                                            disabled 
                                                            value={item.rating}  // Changed from defaultValue to value
                                                            style={{ fontSize: '14px', marginBottom: '4px' }} 
                                                        />
                                                        <Text style={{ marginLeft: '20px',  }}>{item.comment}</Text>
                                                    </>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                />
                            )}
                            {visibleReviews < reviews.length && (
    <Button type="link" onClick={() => setVisibleReviews((prev) => prev + 2)}>
        Load More
    </Button>
)}
                            <Button type="link" icon={<PlusOutlined />} onClick={showModal}>
                                Add a Review
                            </Button>
                        </Panel>
                    </Collapse>
                </div>
            </Card>

            <Modal
                title="Add a Review"
                visible={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                okText="Submit"
                cancelText="Cancel"
            >
                <Form
                    form={form}
                    layout="vertical"
                    name="reviewForm"
                >
                    {/* Removed the username field as per requirement */}
                    <Form.Item
                        name="rating"
                        label="Rating"
                        rules={[{ required: true, message: 'Please provide a rating!' }]}
                    >
                        <Rate />
                    </Form.Item>
                    <Form.Item
                        name="comment"
                        label="Comment"
                        rules={[{ required: true, message: 'Please enter your comment!' }]}
                    >
                        <TextArea rows={4} placeholder="Write your review here..." />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    )};

    export default ProductCard;